/**
 * Field Palette Component
 * Sidebar showing available fields to add to the template
 */

import React from 'react';
import { FieldName, FieldType, AVAILABLE_FIELDS, MappingRegion } from './types';

interface FieldPaletteProps {
  activeField: FieldName | null;
  onSelectField: (fieldName: FieldName | null) => void;
  usedFields: FieldName[];
}

const TYPE_COLORS: Record<FieldType, { bg: string; text: string; border: string }> = {
  text: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
  date: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
  currency: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  number: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300' },
  table: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
};

export const FieldPalette: React.FC<FieldPaletteProps> = ({
  activeField,
  onSelectField,
  usedFields,
}) => {
  // Group fields by category
  const fieldGroups = {
    'Account Info': AVAILABLE_FIELDS.filter(f =>
      ['accountHolderName', 'accountHolderAddress', 'accountHolderAddressLine1',
       'accountHolderAddressLine2', 'accountHolderAddressLine3', 'accountNumber', 'routingNumber'].includes(f.name)
    ),
    'Statement Period': AVAILABLE_FIELDS.filter(f =>
      ['statementPeriodStart', 'statementPeriodEnd', 'statementPeriod'].includes(f.name)
    ),
    'Balances & Totals': AVAILABLE_FIELDS.filter(f =>
      ['openingBalance', 'closingBalance', 'totalDeposits', 'totalWithdrawals',
       'numberOfDeposits', 'numberOfWithdrawals'].includes(f.name)
    ),
    'Transactions': AVAILABLE_FIELDS.filter(f => f.name === 'transactionTable'),
    'Page Info': AVAILABLE_FIELDS.filter(f =>
      ['pageNumber', 'totalPages'].includes(f.name)
    ),
    'Bank Info': AVAILABLE_FIELDS.filter(f =>
      ['bankName', 'bankAddress'].includes(f.name)
    ),
    'Custom Fields': AVAILABLE_FIELDS.filter(f => f.name.startsWith('customField')),
  };

  const isFieldUsed = (fieldName: FieldName) => {
    // Custom fields can be used multiple times
    if (fieldName.startsWith('customField')) return false;
    return usedFields.includes(fieldName);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Instructions</h3>
        <p className="text-xs text-gray-500">
          Click a field below, then click and drag on the PDF to draw the region where that field should appear.
        </p>
      </div>

      {activeField && (
        <div className="p-3 bg-blue-100 border border-blue-300 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              Drawing: {AVAILABLE_FIELDS.find(f => f.name === activeField)?.label}
            </span>
            <button
              onClick={() => onSelectField(null)}
              className="text-blue-600 hover:text-blue-800 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {Object.entries(fieldGroups).map(([groupName, fields]) => (
        <div key={groupName}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {groupName}
          </h4>
          <div className="space-y-1">
            {fields.map(field => {
              const isUsed = isFieldUsed(field.name);
              const isActive = activeField === field.name;
              const colors = TYPE_COLORS[field.type];

              return (
                <button
                  key={field.name}
                  onClick={() => !isUsed && onSelectField(isActive ? null : field.name)}
                  disabled={isUsed}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg border transition-all
                    flex items-center gap-2 text-sm
                    ${isActive
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : isUsed
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : `${colors.bg} ${colors.text} ${colors.border} hover:shadow-sm cursor-pointer`
                    }
                  `}
                  title={field.description}
                >
                  <span className="text-lg">{field.icon}</span>
                  <span className="flex-1 truncate">{field.label}</span>
                  {isUsed && (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="pt-4 border-t">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Legend
        </h4>
        <div className="space-y-1 text-xs">
          {Object.entries(TYPE_COLORS).map(([type, colors]) => (
            <div key={type} className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded ${colors.bg} border ${colors.border}`} />
              <span className="text-gray-600 capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldPalette;
